import { useState, useEffect } from 'react';
import { Coffee, Send, Users, Check, X, Clock, RefreshCw, Mail, Edit, Save, Eye } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminBrunch = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingToGuest, setSendingToGuest] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [template, setTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, templateRes] = await Promise.all([
        api.get('/admin/brunch/stats'),
        api.get('/admin/brunch/template')
      ]);
      setStats(statsRes.data);
      setTemplate(templateRes.data.template || '');
      setSubject(templateRes.data.subject || '');
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/brunch/stats');
      setStats(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const handleSendEmails = async () => {
    setSendingEmail(true);
    try {
      const res = await api.post('/admin/brunch/send-notification');
      toast.success(`${res.data.sent_count} emails envoyés avec succès !`);
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendToGuest = async (rsvpId, email) => {
    setSendingToGuest(rsvpId);
    try {
      await api.post(`/admin/brunch/send-to-guest/${rsvpId}`);
      toast.success(`Email envoyé à ${email}`);
    } catch (error) {
      toast.error(`Erreur: ${error.response?.data?.detail || 'Erreur lors de l\'envoi'}`);
    } finally {
      setSendingToGuest(null);
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.put('/admin/brunch/template', { template, subject });
      toast.success('Template sauvegardé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-charcoal flex items-center gap-2" data-testid="brunch-title">
              <Coffee className="w-7 h-7 text-amber-600" />
              Brunch du Dimanche 6 Septembre
            </h1>
            <p className="text-stone mt-1">Gérez les inscriptions au brunch et envoyez des notifications</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={loadStats}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              className="gap-2 bg-terracotta hover:bg-terracotta/90"
              data-testid="send-brunch-email-btn"
            >
              <Mail className="w-4 h-4" />
              Envoyer l'invitation brunch
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-charcoal">{stats?.total_confirmed_guests || 0}</div>
              <p className="text-sm text-stone">Invités confirmés</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6 text-center">
              <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-700">{stats?.brunch_yes || 0}</div>
              <p className="text-sm text-green-600">Présents au brunch</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <X className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-red-700">{stats?.brunch_no || 0}</div>
              <p className="text-sm text-red-600">Absents au brunch</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6 text-center">
              <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-amber-700">{stats?.brunch_pending || 0}</div>
              <p className="text-sm text-amber-600">En attente de réponse</p>
            </CardContent>
          </Card>
          <Card className="border-terracotta bg-terracotta/10">
            <CardContent className="pt-6 text-center">
              <Coffee className="w-8 h-8 text-terracotta mx-auto mb-2" />
              <div className="text-2xl font-bold text-terracotta">{stats?.brunch_total_people || 0}</div>
              <p className="text-sm text-terracotta">Personnes au brunch</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="list" className="space-y-4">
          <TabsList>
            <TabsTrigger value="list">Liste des invités</TabsTrigger>
            <TabsTrigger value="template">Template email</TabsTrigger>
          </TabsList>

          {/* Liste des invités */}
          <TabsContent value="list">
            <Card>
              <CardHeader>
                <CardTitle>Détail des réponses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-bone/50">
                      <tr>
                        <th className="text-left p-3 font-medium text-charcoal">Famille</th>
                        <th className="text-left p-3 font-medium text-charcoal">Email</th>
                        <th className="text-center p-3 font-medium text-charcoal">Adultes</th>
                        <th className="text-center p-3 font-medium text-charcoal">Enfants</th>
                        <th className="text-center p-3 font-medium text-charcoal">Brunch</th>
                        <th className="text-center p-3 font-medium text-charcoal">Nb pers.</th>
                        <th className="text-center p-3 font-medium text-charcoal">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-light">
                      {stats?.details?.map((guest) => (
                        <tr key={guest.rsvp_id} className="hover:bg-bone/30">
                          <td className="p-3 font-medium text-charcoal">{guest.family_name}</td>
                          <td className="p-3 text-stone text-sm">{guest.email}</td>
                          <td className="p-3 text-center">{guest.adults_count}</td>
                          <td className="p-3 text-center">{guest.children_count}</td>
                          <td className="p-3 text-center">
                            {guest.brunch_attending === true && (
                              <Badge className="bg-green-100 text-green-700">Oui</Badge>
                            )}
                            {guest.brunch_attending === false && (
                              <Badge className="bg-red-100 text-red-700">Non</Badge>
                            )}
                            {guest.brunch_attending === null && (
                              <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                            )}
                          </td>
                          <td className="p-3 text-center font-medium">
                            {guest.brunch_attending === true ? guest.brunch_count || '-' : '-'}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendToGuest(guest.rsvp_id, guest.email)}
                              disabled={sendingToGuest === guest.rsvp_id}
                              className="gap-1"
                            >
                              {sendingToGuest === guest.rsvp_id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Mail className="w-3 h-3" />
                              )}
                              Envoyer
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(!stats?.details || stats.details.length === 0) && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-stone">
                            Aucun invité confirmé pour le moment
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Template email */}
          <TabsContent value="template">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit className="w-5 h-5" />
                    Éditeur de template
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Sujet de l'email</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Sujet de l'email"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Contenu du message (HTML)</Label>
                    <Textarea
                      value={template}
                      onChange={(e) => setTemplate(e.target.value)}
                      placeholder="Contenu HTML de l'email..."
                      className="mt-1 font-mono text-sm"
                      rows={15}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                      className="gap-2 bg-terracotta hover:bg-terracotta/90"
                    >
                      {savingTemplate ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Sauvegarder
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowPreview(true)}
                      className="gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Prévisualiser
                    </Button>
                  </div>
                  <div className="text-xs text-stone bg-bone p-3 rounded-lg">
                    <p className="font-medium mb-2">Variables disponibles :</p>
                    <p>• Le nom de l'invité est ajouté automatiquement dans l'en-tête</p>
                    <p>• Utilisez <code className="bg-white px-1 rounded">&lt;div class="highlight"&gt;...&lt;/div&gt;</code> pour un encadré</p>
                    <p>• Utilisez <code className="bg-white px-1 rounded">&lt;span class="emoji"&gt;📅&lt;/span&gt;</code> pour les emojis</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Aperçu
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="border rounded-lg p-4 bg-white overflow-auto max-h-[500px]"
                    style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}
                  >
                    <div style={{ background: 'linear-gradient(135deg, #D4A373 0%, #C9A227 100%)', padding: '20px', textAlign: 'center', borderRadius: '8px 8px 0 0' }}>
                      <h1 style={{ color: 'white', margin: 0, fontSize: '24px' }}>🥐 Brunch du Dimanche</h1>
                    </div>
                    <div style={{ padding: '20px', color: '#4A4238', lineHeight: 1.8 }}>
                      <h2 style={{ color: '#D4A373', marginTop: 0 }}>Bonjour [Prénom Nom] !</h2>
                      <div 
                        dangerouslySetInnerHTML={{ __html: template.replace(/class="highlight"/g, 'style="background: #FFF8F0; padding: 15px; border-radius: 8px; border-left: 4px solid #D4A373; margin: 15px 0;"').replace(/class="emoji"/g, 'style="font-size: 20px;"') }}
                      />
                      <p style={{ textAlign: 'center', marginTop: '20px' }}>
                        <span style={{ display: 'inline-block', background: '#D4A373', color: 'white', padding: '12px 30px', borderRadius: '50px', fontWeight: 600 }}>
                          Confirmer ma présence au brunch
                        </span>
                      </p>
                      <p style={{ marginTop: '20px' }}>
                        À très vite,<br />
                        <strong style={{ color: '#D4A373' }}>Mélanie & Cédric</strong>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-terracotta" />
              Envoyer l'invitation au brunch
            </DialogTitle>
            <DialogDescription>
              Un email sera envoyé à tous les invités ayant confirmé leur présence au mariage pour les inviter au brunch du dimanche.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <p className="text-amber-800 text-sm">
              <strong>{stats?.total_confirmed_guests || 0} invités</strong> recevront cet email.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSendEmails}
              disabled={sendingEmail}
              className="bg-terracotta hover:bg-terracotta/90 gap-2"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Envoyer les emails
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBrunch;
